import React, { useState, useEffect } from 'react';
import './JsonFeedback.css';
import { applyInlineAnnotations, formatFeedbackContent } from '../utils/annotationHelpers';
import { getColorMapForCategory } from '../utils/colorMaps.js';

/**
 * Component for rendering JSON-structured feedback
 * Handles paragraph-by-paragraph display with inline annotations and feedback
 */
function JsonFeedback({ 
  jsonData, 
  category, 
  commentRatings, 
  handleCommentRating, 
  userData,
  // Feedback editing props
  handleEditFeedback, 
  handleDeleteFeedback, 
  handleAddFeedback,
  isEditingFeedback, 
  editableFeedback, 
  updatedFeedbackContent,
  setUpdatedFeedbackContent, 
  handleSaveEditedFeedback, 
  handleCancelEdit,
  feedbackUpdateStatus, 
  isAddingFeedback, 
  addFeedbackParaIndex,
  newFeedbackContent, 
  setNewFeedbackContent, 
  newFeedbackType,
  setNewFeedbackType, 
  handleSaveNewFeedback, 
  handleCancelAddFeedback,
  getFeedbackTypeOptions,
  // Style objects
  commonInputStyle,
  commonButtonStyle,
  secondaryButtonStyle
}) {
  if (!jsonData || !jsonData.paragraphs) {
    return <div className="json-feedback-container">No feedback available</div>;
  }

  // Get color map from centralized utils
  const colorMap = getColorMapForCategory(category);

  const paragraphs = jsonData.paragraphs || [];
  
  // Helper function to fix annotation positions
  const fixAnnotationPositions = (annotations, paragraphText) => {
    if (!annotations || !paragraphText) {
      return [];
    }
    
    return annotations.map(annotation => {
      const annotationText = annotation.text;
      
      if (!annotationText) {
        return null; // Skip invalid annotations
      }
      
      // Strategy 1: Try exact match first
      let position = paragraphText.indexOf(annotationText);
      if (position !== -1) {
        return {
          ...annotation,
          start: position,
          end: position + annotationText.length
        };
      }

      // Strategy 2: Try case-insensitive match
      const lowerParagraph = paragraphText.toLowerCase();
      const lowerAnnotation = annotationText.toLowerCase();
      position = lowerParagraph.indexOf(lowerAnnotation);
      if (position !== -1) {
        return {
          ...annotation,
          start: position,
          end: position + annotationText.length
        };
      }

      // Strategy 3: Try common word variations and prefixes
      const variations = [
        `To ${annotationText}`,
        `${annotationText}ly`,
        `${annotationText}ed`,
        `${annotationText}ing`,
        `${annotationText}s`,
        annotationText.slice(0, -1),
        annotationText.slice(0, -2),
      ];

      for (const variation of variations) {
        position = paragraphText.indexOf(variation);
        if (position !== -1) {
          return {
            ...annotation,
            start: position,
            end: position + variation.length
          };
        }
        
        position = lowerParagraph.indexOf(variation.toLowerCase());
        if (position !== -1) {
          return {
            ...annotation,
            start: position,
            end: position + variation.length
          };
        }
      }

      // Strategy 4: Try partial matching
      const words = paragraphText.split(/\s+/);
      for (let i = 0; i < words.length; i++) {
        const word = words[i];
        if (word.toLowerCase().includes(lowerAnnotation) || lowerAnnotation.includes(word.toLowerCase())) {
          const wordStart = paragraphText.indexOf(word, i > 0 ? paragraphText.indexOf(words[i-1]) + words[i-1].length : 0);
          if (wordStart !== -1) {
            return {
              ...annotation,
              start: wordStart,
              end: wordStart + word.length
            };
          }
        }
      }

      // Strategy 5: Fuzzy matching around expected positions
      if (annotation.start && annotation.end) {
        const searchStart = Math.max(0, annotation.start - 50);
        const searchEnd = Math.min(paragraphText.length, annotation.end + 50);
        const searchArea = paragraphText.substring(searchStart, searchEnd);
        
        const searchWords = searchArea.split(/\s+/);
        for (const word of searchWords) {
          if (word.toLowerCase().includes(lowerAnnotation) || lowerAnnotation.includes(word.toLowerCase())) {
            const wordStart = paragraphText.indexOf(word, searchStart);
            if (wordStart !== -1) {
              return {
                ...annotation,
                start: wordStart,
                end: wordStart + word.length
              };
            }
          }
        }
      }

      // If all strategies fail, return null to filter out this annotation
      console.warn(`Annotation text "${annotationText}" not found in paragraph, skipping annotation`);
      return null;
    }).filter(annotation => annotation !== null); // Remove null annotations
  };

  return (
    <div className="feedback-columns">
      {paragraphs.map((paragraph, index) => {
        // Use the content from the paragraph (now available for all formats including Australian)
        const { content = '', inline_annotations = [], feedback = [] } = paragraph;
        
        // Fix annotation positions
        const fixedAnnotations = fixAnnotationPositions(inline_annotations, content);
        
        // Get text segments with highlighting
        const textSegments = applyInlineAnnotations(content, fixedAnnotations, colorMap);
        
        return (
          <div key={index} className="paragraph-container">
            <div className="paragraph-text">
              <div>
                {textSegments.map((segment, segmentIndex) => {
                  // Inline highlighting disabled - render all segments as plain text
                  return <span key={segmentIndex}>{segment.text}</span>;
                })}
              </div>
            </div>
            
            <div className="feedback-column">
              {feedback.length > 0 ? (
                feedback.map((fb, fbIndex) => {
                  const ratingKey = `${index}-${fbIndex}`;
                  const currentRating = commentRatings[ratingKey];
                  
                  // Check if this feedback is being edited
                  const isThisFeedbackBeingEdited = isEditingFeedback && 
                    editableFeedback?.paraIndex === index && 
                    editableFeedback?.fbIndex === fbIndex;
                  
                  return (
                    <div key={fbIndex} className="feedback-item">
                      {isThisFeedbackBeingEdited ? (
                        // Edit interface
                        <div className="feedback-edit-container">
                          <textarea
                            value={updatedFeedbackContent}
                            onChange={(e) => setUpdatedFeedbackContent(e.target.value)}
                            className="feedback-edit-textarea"
                            rows={4}
                            style={commonInputStyle}
                          />
                          <div className="feedback-edit-buttons">
                            <button 
                              onClick={handleSaveEditedFeedback}
                              style={commonButtonStyle}
                              disabled={feedbackUpdateStatus === 'saving'}
                            >
                              {feedbackUpdateStatus === 'saving' ? 'Saving...' : 'Save'}
                            </button>
                            <button 
                              onClick={handleCancelEdit}
                              style={secondaryButtonStyle}
                            >
                              Cancel
                            </button>
                          </div>
                          {feedbackUpdateStatus && (
                            <div className={`feedback-update-status feedback-update-${feedbackUpdateStatus}`}>
                              {feedbackUpdateStatus === 'success' && '✓ Saved successfully'}
                              {feedbackUpdateStatus === 'error' && '⚠ Error saving'}
                            </div>
                          )}
                        </div>
                      ) : (
                        // Normal display
                        <>
                          <div 
                            style={{
                              padding: '8px 12px',
                              marginBottom: '8px',
                              borderLeft: `4px solid ${colorMap[fb.type] || '#3498db'}`,
                              backgroundColor: `${colorMap[fb.type] || '#3498db'}22`,
                              borderRadius: '0 4px 4px 0',
                              position: 'relative',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              gap: '10px'
                            }}
                          >
                            <div 
                              dangerouslySetInnerHTML={{ __html: formatFeedbackContent(fb.content, category) }}
                              style={{ flex: 1 }}
                            />
                            
                            {/* Controls */}
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: '1fr 1fr',
                              gap: '2px',
                              flexShrink: 0,
                              minWidth: '60px'
                            }}>
                              <button
                                onClick={() => handleCommentRating(index, fbIndex, 'helpful')}
                                className={`rating-button ${currentRating === 'helpful' ? 'rating-button-up-selected' : ''}`}
                                title="This feedback was helpful"
                                style={{
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  padding: '2px 4px'
                                }}
                              >
                                👍
                              </button>
                              
                              {userData?.role === 'teacher' ? (
                                <button
                                  onClick={() => handleEditFeedback(index, fbIndex, `{${fb.type}: ${fb.content}}`)}
                                  className="teacher-feedback-button edit-button"
                                  title="Edit feedback"
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    padding: '2px 4px'
                                  }}
                                >
                                  ✏️
                                </button>
                              ) : (
                                <div></div>
                              )}
                              
                              <button
                                onClick={() => handleCommentRating(index, fbIndex, 'not_helpful')}
                                className={`rating-button ${currentRating === 'not_helpful' ? 'rating-button-down-selected' : ''}`}
                                title="This feedback was not helpful"
                                style={{
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  padding: '2px 4px'
                                }}
                              >
                                👎
                              </button>
                              
                              {userData?.role === 'teacher' ? (
                                <button
                                  onClick={() => handleDeleteFeedback(index, fbIndex)}
                                  className="teacher-feedback-button delete-button"
                                  title="Delete feedback"
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    padding: '2px 4px'
                                  }}
                                >
                                  🗑️
                                </button>
                              ) : (
                                <div></div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="no-feedback-message">
                  No feedback for this paragraph
                </div>
              )}
              
              {/* Add feedback for teachers */}
              {userData?.role === 'teacher' && (
                <div className="add-feedback-container">
                  {isAddingFeedback && addFeedbackParaIndex === index ? (
                    <div className="add-feedback-form">
                      <div className="feedback-type-selector">
                        <label>Feedback Type:</label>
                        <select
                          value={newFeedbackType}
                          onChange={(e) => setNewFeedbackType(e.target.value)}
                          className="feedback-type-select"
                        >
                          {getFeedbackTypeOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <textarea
                        value={newFeedbackContent}
                        onChange={(e) => setNewFeedbackContent(e.target.value)}
                        placeholder="Enter your feedback..."
                        className="add-feedback-textarea"
                        rows={4}
                        style={commonInputStyle}
                      />
                      <div className="add-feedback-buttons">
                        <button 
                          onClick={handleSaveNewFeedback}
                          style={commonButtonStyle}
                          disabled={!newFeedbackContent.trim() || feedbackUpdateStatus === 'saving'}
                        >
                          {feedbackUpdateStatus === 'saving' ? 'Adding...' : 'Add Feedback'}
                        </button>
                        <button 
                          onClick={handleCancelAddFeedback}
                          style={secondaryButtonStyle}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleAddFeedback(index)}
                      className="add-feedback-button"
                      title="Add new feedback"
                    >
                      + Add Feedback
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default JsonFeedback; 
